import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface ClipItem {
  id: string;
  title: string;
  score: number;
  duration: number;
  file_url: string;
  video_id: string;
  video_file_name: string;
  project_id: string | null;
  project_name: string | null;
  created_at: string;
  edit_version: number;
  export_status: string | null;
}

const ClipList: React.FC = () => {
  const [clips, setClips] = useState<ClipItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('score');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}/api/clips?page=${page}&limit=20&sort=${sort}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { clips: [], total: 0 })
      .then(data => {
        setClips(data.clips || []);
        setTotal(data.total || 0);
      })
      .catch(() => setClips([]))
      .finally(() => setLoading(false));
  }, [page, sort]);

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content">
        <div className="clip-list-page">
          <div className="clip-list-header">
            <h1>🎬 All Clips</h1>
            <div className="clip-list-controls">
              <select value={sort} onChange={e => { setSort(e.target.value); setPage(1); }}>
                <option value="score">Sort by Score</option>
                <option value="date">Sort by Date</option>
                <option value="duration">Sort by Duration</option>
              </select>
              <span className="clip-count">{total} clips</span>
            </div>
          </div>

          {loading ? (
            <div className="clip-list-loading">Loading clips...</div>
          ) : clips.length === 0 ? (
            <div className="clip-list-empty">
              <p>No clips yet. Upload a video and process it to generate clips.</p>
              <Link to="/projects" className="btn-primary">Go to Projects</Link>
            </div>
          ) : (
            <>
              <div className="clip-list-grid">
                {clips.map(clip => (
                  <Link
                    key={clip.id}
                    to={clip.project_id
                      ? `/projects/${clip.project_id}/video/${clip.video_id}/clip/${clip.id}`
                      : `/clips/${clip.id}`}
                    className="clip-list-card"
                  >
                    <div className="clip-list-card-thumb">
                      <video src={`${API_BASE}${clip.file_url}`} preload="metadata" muted />
                      <div className="clip-list-card-overlay">
                        <span className="clip-score-badge">{clip.score}</span>
                        <span className="clip-duration">{clip.duration?.toFixed(1)}s</span>
                      </div>
                    </div>
                    <div className="clip-list-card-info">
                      <h3>{clip.title || `Clip #${clip.id.slice(0, 6)}`}</h3>
                      <p className="clip-meta">
                        {clip.project_name || 'No Project'} · {clip.video_file_name}
                      </p>
                      <div className="clip-badges">
                        {clip.edit_version > 0 && <span className="badge badge-edited">Edited</span>}
                        {clip.export_status === 'completed' && <span className="badge badge-exported">Exported</span>}
                        {/* An export is a job now, and a running one reports its stage rather
                            than a single "processing" — anything not yet finished is in flight. */}
                        {!!clip.export_status && !['completed', 'failed'].includes(clip.export_status) && (
                          <span className="badge badge-processing">Processing</span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="clip-list-pagination">
                  <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                  <span>Page {page} of {totalPages}</span>
                  <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ClipList;
